<script setup>
import { Inertia } from "@inertiajs/inertia";
import { ref, onMounted, watch } from "vue";
import { useForm } from "@inertiajs/inertia-vue3";
import WidgetLayout from "@/Layouts/WidgetLayout.vue";
import Label from "@/Components/Settings/Label.vue";
import Select from "@/Components/Select.vue";
import AppointmentTypeSearch from "@/Components/Widget/AppointmentTypeSearch.vue";
import RadioButtons from "@/Components/Widget/RadioButtons.vue";

const props = defineProps({
  office: Object,
  types: Object,
  widgetkey: String,
  widgeteid: String,
  errors: Object,

  type_id: Number,
  practitioner_id: Number,
  insurance: Number,
  video: Boolean,
  pain: Boolean,
  mo: String,
  di: String,
  mi: String,
  do: String,
  fr: String,
  month: String,
});

const errors = ref(props.errors);

const form = useForm({
  type_id: props.type_id || null,
  practitioner_id: props.practitioner_id || null,
  insurance: props.insurance || null,
  video: props.video || false,
  pain: props.pain || false,
  mo: props.mo || null,
  di: props.di || null,
  mi: props.mi || null,
  do: props.do || null,
  fr: props.fr || null,
  month: parseInt(props.month) || null,
});

const practitioners = ref(props.office.practitioners);
const types = ref(props.types);
const months = ref([]);

let pymChild = null;

// watch(
//   form,
//   (cur, old) => {
//     console.log(cur, old);
//   },
//   { deep: true }
// );

// watch errors
watch(
  () => props.errors,
  (errors) => {
    if (pymChild) {
      setTimeout(() => {
        pymChild.sendHeight();
      }, 200);
    }
  },
  { deep: true }
);

function pymLoaded() {
  pymChild = new pym.Child();
}

onMounted(() => {
  // initiate pym
  const plugin = document.createElement("script");
  plugin.setAttribute("src", "https://pym.nprapps.org/pym.v1.min.js");
  plugin.async = true;
  plugin.onreadystatechange = pymLoaded;
  plugin.onload = pymLoaded;
  document.head.appendChild(plugin);

  // fill next 6 months
  const today = new Date();
  const month = today.getMonth();
  if (!form.month) {
    form.month = month + 1;
  }
  const year = today.getFullYear();
  for (let i = 0; i < 6; i++) {
    const date = new Date(year, month + i, 1);
    months.value.push([
      date.getMonth() + 1,
      date.toLocaleString("default", { month: "long" }) +
        " " +
        date.getFullYear(),
    ]);
  }

  // fill initial when values are set (from using back button)
  if (form.type_id) {
    for (const [i, type] of Object.entries(types.value)) {
      if (type.id == form.type_id) {
        changeType(type.id);
      }
    }
  }
});

function changeType(id) {
  form.type_id = id;
  const type = types.value.find((type) => type.id == form.type_id);

  for (const [i1, practitioner] of Object.entries(practitioners.value)) {
    practitioner.isDisabled = true;
    for (const [i2, typePract] of Object.entries(type.practitioners)) {
      if (practitioner.id == typePract.id) {
        practitioner.isDisabled = false;
      }
    }

    if (form.practitioner_id == practitioner.id && practitioner.isDisabled) {
      form.reset("practitioner_id");
    }
  }
}

function changePractitioner(event) {
  form.practitioner_id = parseInt(event.target.value);
}

function submitForm() {
  // validate form
  let hasErrors = false;

  if (!form.type_id) {
    errors.value.type_id = "Bitte Behandlungstyp auswählen";
    hasErrors = true;
  }

  if (!form.insurance) {
    errors.value.insurance = "Bitte Versicherungsart auswählen";
    hasErrors = true;
  }

  if (!form.mo && !form.di && !form.mi && !form.do && !form.fr) {
    errors.value.mo = "Bitte mindestens einen Zeitraum auswählen";
    hasErrors = true;
  }

  if (!hasErrors) {
    let url = route("widget.iframe.step2", { key: props.widgetkey });
    Inertia.visit(url, {
      method: "get",
      data: {
        eid: props.widgeteid,
        type_id: form.type_id,
        practitioner_id: form.practitioner_id,
        insurance: form.insurance,
        video: form.video,
        pain: form.pain,
        mo: form.mo,
        di: form.di,
        mi: form.mi,
        do: form.do,
        fr: form.fr,
        month: form.month,
      },
      preserveState: true,
    });
  }
}
</script>

<template>
  <WidgetLayout :office="props.office">
    <h2 class="text-3xl font-semibold mb-8">Buchen Sie Ihren Termin online</h2>

    <div class="lg:flex">
      <div class="lg:w-1/2 lg:pr-16 lg:border-r">
        <!-- left -->

        <div class="mt-8 relative z-10">
          <Label for="types" value="Behandlungstyp" class="text-lg" />

          <div class="flex gap-4 mt-1 relative">
            <!-- <AppointmentTypeSearch
              :types="types"
              :modelValue="form.type_id"
              @selectType="(type) => changeType(type)"
            >
            </AppointmentTypeSearch
            > --><!-- v-model="form.type_id" -->

            <Select
              id="type_id"
              class="mt-1 block w-full"
              v-model="form.type_id"
              @change="changeType($event.target.value)"
            >
              <option disabled value="">Behandlungstyp auswählen</option>

              <option v-for="type in types" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </Select>
          </div>

          <div class="mt-2 text-red-600 text-sm" v-if="errors.type_id">
            {{ errors.type_id }}
          </div>
        </div>

        <div class="mt-8">
          <Label for="user_id" value="Behandler/in" class="text-lg" />

          <Select
            id="user_id"
            class="mt-1 block w-full"
            v-model="form.practitioner_id"
            @change="changePractitioner($event)"
            :disabled="form.pain"
          >
            <option disabled value="">Behandler/in auswählen</option>

            <option
              v-for="user in practitioners"
              :key="user.id"
              :value="user.id"
              :disabled="user.isDisabled"
            >
              {{ user.fullname }}
            </option>
          </Select>

          <div class="mt-2 text-red-600 text-sm" v-if="errors.practitioner_id">
            {{ errors.practitioner_id }}
          </div>
        </div>

        <div class="mt-11 pb-8 relative z-0">
          <Label for="insurance" value="Versicherungsart" class="text-lg" />

          <!-- <RadioButtons
                        v-model="form.insurance"
                        :name="'insurance'"
                        :options="{1: 'Gesetzlich', 2: 'Privat'}"
                    /> -->

          <div class="flex items-center">
            <input
              type="radio"
              id="insurance1"
              name="insurance"
              value="1"
              v-model="form.insurance"
              class="mr-2"
            />
            <label for="insurance1" class="mr-8">Gesetzlich</label>

            <input
              type="radio"
              id="insurance2"
              name="insurance"
              value="2"
              v-model="form.insurance"
              class="mr-2"
            />
            <label for="insurance2">Privat</label>
          </div>

          <div class="mt-2 text-red-600 text-sm" v-if="errors.insurance">
            {{ errors.insurance }}
          </div>
        </div>

        <!--<div class="mt-8">
                    <div class="flex items-center">
                        <input
                            class="mr-4"
                            type="checkbox"
                            v-model="form.video"
                            :selected="form.video == 'video'"
                            value="video"
                            name="video"
                            id="video"
                        />
                        <label for="video" class="text-lg font-semibold"
                            >Videosprechstunde</label
                        >
                    </div>

                    <div class="pl-8 text-sm">
                        <label for="video">
                            Lorem ipsum, Dolor sit, Consetetur, Lorem ipsum
                            dolor
                        </label>
                    </div>
                </div>

                <div class="mt-8 mb-8 pb-8">
                    <div class="flex items-center">
                        <input
                            class="mr-4"
                            type="checkbox"
                            v-model="form.pain"
                            @change="($event) => (form.practitioner_id = null)"
                            :selected="form.pain == 'pain'"
                            value="pain"
                            name="pain"
                            id="pain"
                        />
                        <label for="pain" class="text-lg font-semibold"
                            >Ich habe Schmerzen</label
                        >
                    </div>

                    <div class="pl-8 text-sm">
                        <label for="pain">
                            Bei Schmerzen wird Ihnen der nächste verfügbare
                            Termin unabhängig vom Behandler angeboten.
                        </label>
                    </div>
                </div>-->
      </div>

      <div class="lg:w-1/2 lg:pl-16">
        <!-- right -->
        <div class="mt-8">
          <Label for="types" value="Wochentag auswählen" class="text-lg" />

          <table>
            <tr>
              <td class="pb-3">
                <div class="font-semibold mr-8">Mo</div>
              </td>

              <td class="pb-3">
                <div class="sm:flex justify-center">
                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.mo = 'mo_full')
                            : (form.mo = null)
                      "
                      :checked="form.mo === 'mo_full'"
                      value="mo_full"
                      name="mo_full"
                      id="mo_full"
                    />
                    <label for="mo_full" class="select-none">Ganztags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.mo = 'mo_am')
                            : (form.mo = null)
                      "
                      :checked="form.mo === 'mo_am'"
                      value="mo_am"
                      name="mo_am"
                      id="mo_am"
                    />
                    <label for="mo_am" class="select-none">Vormittags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.mo = 'mo_pm')
                            : (form.mo = null)
                      "
                      :checked="form.mo === 'mo_pm'"
                      value="mo_pm"
                      name="mo_pm"
                      id="mo_pm"
                    />
                    <label for="mo_pm" class="select-none">Nachmittags</label>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td class="pb-3">
                <div class="font-semibold mr-4">Di</div>
              </td>

              <td class="pb-3">
                <div class="sm:flex justify-center">
                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.di = 'di_full')
                            : (form.di = null)
                      "
                      :checked="form.di === 'di_full'"
                      value="di_full"
                      name="di_full"
                      id="di_full"
                    />
                    <label for="di_full" class="select-none">Ganztags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.di = 'di_am')
                            : (form.di = null)
                      "
                      :checked="form.di === 'di_am'"
                      value="di_am"
                      name="di_am"
                      id="di_am"
                    />
                    <label for="ccdi_am" class="select-none">Vormittags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.di = 'di_pm')
                            : (form.di = null)
                      "
                      :checked="form.di === 'di_pm'"
                      value="di_pm"
                      name="di_pm"
                      id="di_pm"
                    />
                    <label for="di_pm" class="select-none">Nachmittags</label>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td class="pb-3">
                <div class="font-semibold mr-4">Mi</div>
              </td>

              <td class="pb-3">
                <div class="sm:flex justify-center">
                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.mi = 'mi_full')
                            : (form.mi = null)
                      "
                      :checked="form.mi === 'mi_full'"
                      value="mi_full"
                      name="mi_full"
                      id="mi_full"
                    />
                    <label for="mi_full" class="select-none">Ganztags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.mi = 'mi_am')
                            : (form.mi = null)
                      "
                      :checked="form.mi === 'mi_am'"
                      value="mi_am"
                      name="mi_am"
                      id="mi_am"
                    />
                    <label for="mi_am" class="select-none">Vormittags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.mi = 'mi_pm')
                            : (form.mi = null)
                      "
                      :checked="form.mi === 'mi_pm'"
                      value="mi_pm"
                      name="mi_pm"
                      id="mi_pm"
                    />
                    <label for="mi_pm" class="select-none">Nachmittags</label>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td class="pb-3">
                <div class="font-semibold mr-4">Do</div>
              </td>

              <td class="pb-3">
                <div class="sm:flex justify-center">
                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.do = 'do_full')
                            : (form.do = null)
                      "
                      :checked="form.do === 'do_full'"
                      value="do_full"
                      name="do_full"
                      id="do_full"
                    />
                    <label for="do_full" class="select-none">Ganztags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.do = 'do_am')
                            : (form.do = null)
                      "
                      :checked="form.do === 'do_am'"
                      value="do_am"
                      name="do_am"
                      id="do_am"
                    />
                    <label for="do_am" class="select-none">Vormittags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.do = 'do_pm')
                            : (form.do = null)
                      "
                      :checked="form.do === 'do_pm'"
                      value="do_pm"
                      name="do_pm"
                      id="do_pm"
                    />
                    <label for="do_pm" class="select-none">Nachmittags</label>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td class="pb-3">
                <div class="font-semibold mr-4">Fr</div>
              </td>

              <td class="pb-3">
                <div class="sm:flex justify-center">
                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.fr = 'fr_full')
                            : (form.fr = null)
                      "
                      :checked="form.fr === 'fr_full'"
                      value="fr_full"
                      name="fr_full"
                      id="fr_full"
                    />
                    <label for="fr_full" class="select-none">Ganztags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.fr = 'fr_am')
                            : (form.fr = null)
                      "
                      :checked="form.fr === 'fr_am'"
                      value="fr_am"
                      name="fr_am"
                      id="fr_am"
                    />
                    <label for="fr_am" class="select-none">Vormittags</label>
                  </div>

                  <div class="flex items-center mr-3">
                    <input
                      class="mr-1"
                      type="checkbox"
                      @change="
                        ($event) =>
                          $event.target.checked
                            ? (form.fr = 'fr_pm')
                            : (form.fr = null)
                      "
                      :checked="form.fr === 'fr_pm'"
                      value="fr_pm"
                      name="fr_pm"
                      id="fr_pm"
                    />
                    <label for="fr_pm" class="select-none">Nachmittags</label>
                  </div>
                </div>
              </td>
            </tr>
          </table>

          <div
            class="mt-2 text-red-600 text-sm"
            v-if="errors.mo || errors.di || errors.mi || errors.do || errors.fr"
          >
            {{ errors.mo }}
          </div>
        </div>

        <div class="mt-2 pb-8">
          <Label for="month" value="Monat" class="text-lg" />

          <Select id="month" class="mt-1 block w-full" v-model="form.month">
            <option
              v-for="(month, i) in months"
              :key="month[0]"
              :value="month[0]"
              :selected="month[0] === form.month"
            >
              {{ month[1] }}
            </option>
          </Select>

          <div class="mt-2 text-red-600 text-sm" v-if="errors.month">
            {{ errors.month }}
          </div>
        </div>

        <!--<div class="mt-8">
                            <Label for="user_id" value="Zeitraum auswählen" class="text-lg" />

                            <div class="sm:flex items-center">
                                <div class="sm:mr-4 my-2">von</div>

                                <Select
                                    id="user_id"
                                    class="mt-1 block w-[120px]"
                                    v-model="form.from"
                                    required
                                >
                                    <option disabled value="">
                                        Behandler/in auswählen
                                    </option>

                                    <option
                                        v-for="user in office.practitioners"
                                        :key="user.id"
                                        :value="user.id"
                                    >
                                        {{ user.fullname }}
                                    </option>
                                </Select>

                                <div class="sm:mx-4 my-2">bis</div>

                                <Select
                                    id="user_id"
                                    class="mt-1 block w-[120px]"
                                    v-model="form.till"
                                    required
                                >
                                    <option disabled value="">
                                        Behandler/in auswählen
                                    </option>

                                    <option
                                        v-for="user in office.practitioners"
                                        :key="user.id"
                                        :value="user.id"
                                    >
                                        {{ user.fullname }}
                                    </option>
                                </Select>

                                <div class="sm:ml-4 my-2">Uhr</div>
                            </div>
                        </div>-->
      </div>
    </div>

    <div class="mt-12 py-6 text-center">
      <button
        type="button"
        class="px-6 md:px-20 py-3 text-xl font-semibold rounded-lg text-white bg-primary hover:opacity-70 cursor-pointer"
        @click="submitForm()"
      >
        Termine suchen
      </button>
    </div>
  </WidgetLayout>
</template>

<style>
.text-primary {
  color: rgb(0, 110, 180);
}
.bg-primary {
  background-color: #006eb4;
}
</style>
